import React, { useEffect } from 'react';

interface PerformanceMonitorProps {
  enabled?: boolean;
  logToConsole?: boolean;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ 
  enabled = process.env.NODE_ENV === 'development',
  logToConsole = true 
}) => {
  useEffect(() => {
    if (!enabled) return;

    // Monitor Core Web Vitals
    const monitorWebVitals = () => {
      // Largest Contentful Paint (LCP)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (logToConsole) {
          console.log('🎯 LCP (Largest Contentful Paint):', lastEntry.startTime.toFixed(2), 'ms');
        }
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay (FID)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          if (logToConsole) {
            console.log('⚡ FID (First Input Delay):', entry.processingStart - entry.startTime, 'ms');
          }
        });
      }).observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        if (logToConsole) {
          console.log('📐 CLS (Cumulative Layout Shift):', clsValue.toFixed(4));
        }
      }).observe({ entryTypes: ['layout-shift'] });

      // First Contentful Paint (FCP)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const fcp = entries[0];
        if (logToConsole) {
          console.log('🎨 FCP (First Contentful Paint):', fcp.startTime.toFixed(2), 'ms');
        }
      }).observe({ entryTypes: ['paint'] });
    };

    // Monitor image loading performance
    const monitorImagePerformance = () => {
      const images = document.querySelectorAll('img');
      let loadedImages = 0;
      const totalImages = images.length;
      
      if (totalImages === 0) return;

      const startTime = performance.now();

      images.forEach((img) => {
        if (img.complete) {
          loadedImages++;
        } else {
          img.addEventListener('load', () => {
            loadedImages++;
            if (loadedImages === totalImages) {
              const loadTime = performance.now() - startTime;
              if (logToConsole) {
                console.log(`🖼️  All images loaded in ${loadTime.toFixed(2)}ms`);
              }
            }
          });
        }
      });

      if (loadedImages === totalImages) {
        const loadTime = performance.now() - startTime;
        if (logToConsole) {
          console.log(`🖼️  All images loaded in ${loadTime.toFixed(2)}ms`);
        }
      }
    };

    // Monitor resource loading
    const monitorResourceLoading = () => {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.name.includes('.webp') || entry.name.includes('.jpg') || entry.name.includes('.png')) {
            if (logToConsole) {
              console.log(`📦 Image loaded: ${entry.name} in ${entry.duration.toFixed(2)}ms`);
            }
          }
        });
      });
      
      observer.observe({ entryTypes: ['resource'] });
    };

    // Start monitoring
    monitorWebVitals();
    monitorImagePerformance();
    monitorResourceLoading();

    // Log performance summary after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (logToConsole) {
          console.log('🚀 Performance Summary:');
          console.log(`  DOM Content Loaded: ${navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart}ms`);
          console.log(`  Page Load Complete: ${navigation.loadEventEnd - navigation.loadEventStart}ms`);
          console.log(`  Total Page Load: ${navigation.loadEventEnd - navigation.fetchStart}ms`);
        }
      }, 1000);
    });

  }, [enabled, logToConsole]);

  return null; // This component doesn't render anything
};

export default PerformanceMonitor;
